import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Edit, Trash2, Layers, Search, ChevronLeft, ChevronRightIcon, ArrowLeft } from 'lucide-react';
import Header from '../../components/Header';
import { useAuth } from '../../context/AuthContext';
import { circuitsAPI } from '../../utils/api';
import { handleApiError } from '../../utils/api';

const PAGE_SIZE = 20;

const ManageCircuitsPage = () => {
  const navigate = useNavigate();
  const { userRole } = useAuth();

  const [circuits, setCircuits] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(1);
  const [deletingId, setDeletingId] = useState(null);

  const loadCircuits = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await circuitsAPI.getAll();
      setCircuits(response.data || []);
    } catch (err) {
      const { error: errTitle, details } = handleApiError(err);
      setError(`${errTitle} : ${details}`);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadCircuits();
  }, [loadCircuits]);

  if (userRole !== 'admin') {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="container mx-auto px-4 py-8">
          <div className="text-center">
            <h2 className="text-2xl font-bold text-red-600 mb-4">Accès refusé</h2>
            <p className="text-gray-600 mb-6">Vous devez être administrateur pour accéder à cette page.</p>
            <button
              onClick={() => navigate('/')}
              className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition"
            >
              Retour à l'accueil
            </button>
          </div>
        </div>
      </div>
    );
  }

  const handleDelete = async (circuit) => {
    if (!window.confirm(`Supprimer "${circuit.title}" ? Cette action est irréversible.`)) {
      return;
    }
    setDeletingId(circuit.id);
    try {
      await circuitsAPI.delete(circuit.id);
      setCircuits((prev) => prev.filter((c) => c.id !== circuit.id));
    } catch (err) {
      const { error: errTitle, details } = handleApiError(err);
      alert(`Impossible de supprimer : ${errTitle} - ${details}`);
    } finally {
      setDeletingId(null);
    }
  };

  const filteredCircuits = circuits.filter((circuit) => {
    const term = searchTerm.toLowerCase();
    return (
      circuit.title?.toLowerCase().includes(term) ||
      circuit.acronym?.toLowerCase().includes(term) ||
      circuit.organizer?.toLowerCase().includes(term)
    );
  });

  const totalPages = Math.max(1, Math.ceil(filteredCircuits.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const paginatedCircuits = filteredCircuits.slice(
    (currentPage - 1) * PAGE_SIZE,
    currentPage * PAGE_SIZE
  );

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />

      <div className="container mx-auto px-4 py-8">
        <div className="max-w-6xl mx-auto">
          <button
            onClick={() => navigate('/admin')}
            className="text-gray-600 hover:text-blue-600 flex items-center gap-1 mb-4 text-sm"
          >
            <ArrowLeft size={16} />
            Retour à l'administration
          </button>

          {/* Header */}
          <div className="bg-white rounded-lg shadow-lg p-6 mb-6">
            <div className="flex items-center justify-between mb-4 flex-wrap gap-4">
              <div>
                <h1 className="text-3xl font-bold text-gray-800 flex items-center">
                  <Layers className="mr-3" size={32} />
                  Gestion des Circuits
                </h1>
                <p className="text-gray-600 mt-2">
                  {circuits.length} circuit{circuits.length > 1 ? 's' : ''} au total
                </p>
              </div>
              <button
                onClick={() => navigate('/admin/circuits/create')}
                className="bg-gradient-to-r from-blue-600 to-purple-600 text-white px-6 py-3 rounded-lg font-semibold hover:from-blue-700 hover:to-purple-700 transition flex items-center"
              >
                <Plus className="mr-2" size={20} />
                Nouveau Circuit
              </button>
            </div>

            {/* Recherche */}
            <div className="relative">
              <Search className="absolute left-3 top-3 text-gray-400" size={20} />
              <input
                type="text"
                placeholder="Rechercher par titre, acronyme ou organisateur..."
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setPage(1);
                }}
                className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg p-4 mb-6">
              {error}
            </div>
          )}

          {isLoading ? (
            <div className="bg-white rounded-lg shadow p-8 text-center text-gray-500">
              Chargement des circuits...
            </div>
          ) : filteredCircuits.length === 0 ? (
            <div className="bg-white rounded-lg shadow p-8 text-center">
              <p className="text-gray-500">Aucun circuit trouvé</p>
            </div>
          ) : (
            <>
              <div className="bg-white rounded-lg shadow-lg overflow-hidden">
                <table className="w-full">
                  <thead className="bg-gray-100 text-left text-sm text-gray-600">
                    <tr>
                      <th className="px-4 py-3">Titre</th>
                      <th className="px-4 py-3">Acronyme</th>
                      <th className="px-4 py-3">Organisateur</th>
                      <th className="px-4 py-3">Couleur</th>
                      <th className="px-4 py-3 text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {paginatedCircuits.map((circuit) => (
                      <tr key={circuit.id} className="hover:bg-gray-50">
                        <td className="px-4 py-3 font-semibold text-gray-800">{circuit.title}</td>
                        <td className="px-4 py-3 text-gray-600">{circuit.acronym || '—'}</td>
                        <td className="px-4 py-3 text-gray-600">{circuit.organizer || '—'}</td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <span className={`inline-block w-4 h-4 rounded-full ${circuit.color || 'bg-blue-600'}`} />
                            <span className="text-gray-500 text-sm">{circuit.color || '—'}</span>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex justify-end gap-2">
                            <button
                              onClick={() => navigate(`/admin/circuits/edit/${circuit.id}`)}
                              className="bg-blue-600 text-white p-2 rounded-lg hover:bg-blue-700 transition"
                              title="Modifier"
                            >
                              <Edit size={16} />
                            </button>
                            <button
                              onClick={() => handleDelete(circuit)}
                              disabled={deletingId === circuit.id}
                              className="bg-red-600 text-white p-2 rounded-lg hover:bg-red-700 transition disabled:opacity-50"
                              title="Supprimer"
                            >
                              <Trash2 size={16} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex items-center justify-between mt-4">
                  <p className="text-sm text-gray-600">
                    Page {currentPage} sur {totalPages} ({filteredCircuits.length} résultats)
                  </p>
                  <div className="flex gap-2">
                    <button
                      onClick={() => setPage((p) => Math.max(1, p - 1))}
                      disabled={currentPage === 1}
                      className="bg-white border border-gray-300 p-2 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <ChevronLeft size={18} />
                    </button>
                    <button
                      onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                      disabled={currentPage === totalPages}
                      className="bg-white border border-gray-300 p-2 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <ChevronRightIcon size={18} />
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ManageCircuitsPage;
